import campaignStatusApi from 'domain/campaignStatus/api'
import { isCampaignStatusTaskInFlight } from 'domain/campaignStatus/mappers'
import { ClientCampaignStatus } from 'domain/campaignStatus/schemas'
import { CampaignOptimizationStatus } from 'api/models'
import { isErrorCode } from 'utils/helpers'
import moment from 'moment'

// `error` is the already-unwrapped envelope body ({ error_code, message },
// a plain string, or an axios Error) — codifyAPIError() expects a raw axios
// error, not this shape, so it can't be reused directly here.
function codifyCampaignStatusError(error) {
  if (!error) {
    return 'common.errors.inServerResponse'
  }

  const errorCode = typeof error === 'string' ? error : (error.error_code ?? error.message)
  return isErrorCode(errorCode) ? `common.serverErrors.${errorCode}` : 'common.errors.inServerResponse'
}

export const actions = Object.freeze({
  fetchCampaignStatusAsync: 'FETCH_CAMPAIGN_STATUS_ASYNC',
  toggleCampaignStatusAsync: 'TOGGLE_CAMPAIGN_STATUS_ASYNC',
  togglePlatformCampaignStatusAsync: 'TOGGLE_PLATFORM_CAMPAIGN_STATUS_ASYNC',
  toggleCampaignOptimizationAsync: 'CHANGE_CAMPAIGN_OPTIMIZATION_ASYNC',
  toggleAdCampaignOptimizationAsync: 'CHANGE_AD_CAMPAIGN_OPTIMIZATION_ASYNC',
  setCampaignStatus: 'SET_CAMPAIGN_STATUS',
  setCampaignOptimizationStatus: 'SET_CAMPAIGN_OPTIMIZATION_CHANGED',
  setAdCampaignOptimizationStatus: 'SET_AD_CAMPAIGN_OPTIMIZATION_CHANGED',
  setError: 'SET_ERROR',
  setLoading: 'SET_LOADING',
  resetMarketingCampaignStatus: 'RESET_MARKETING_CAMPAIGN_STATUS',
})

const _initialState = {
  loading: false,
  error: null,
  isPastEvent: false,
  // Full domain/campaignStatus getEventMarketingCampaignState() shape, or
  // null before the first fetch resolves — replaces v3's flat
  // hasMarketingCampaign/hasActiveCampaign/hasEndedCampaign booleans, which
  // couldn't represent Google/Meta having independent states.
  campaignStatus: null,
  isCampaignOptimizationOn: false,
  isAdCampaignOptimizationOn: false,
  success: false,
}

// campaign_optimisation_status/ad_campaign_optimisation_status stay flat
// fields on `event` in v4 too (separate routes, not folded into
// campaign_status — see domain/campaignStatus/api.js) so these two still
// derive from `event` directly; campaignStatus itself now requires a fetch
// (v4's per-platform data isn't baked onto the event object the way v3's
// flat campaign_status/campaign_stop_time were).
export const initialState = (event) => ({
  ..._initialState,
  isCampaignOptimizationOn:
    event?.campaign_optimisation_status != CampaignOptimizationStatus.paused,
  isAdCampaignOptimizationOn:
    event?.ad_campaign_optimisation_status != CampaignOptimizationStatus.paused,
  isPastEvent: moment(event?.date) < moment(),
})

export const reducer = (state, action) => {
  switch (action.type) {
    case actions.setLoading:
      return { ...state, loading: action.payload }
    case actions.setCampaignStatus:
      return {
        ...state,
        campaignStatus: action.payload,
        success: true,
        loading: false,
      }
    case actions.setError:
      return { ...state, error: action.payload, loading: false }
    case actions.setCampaignOptimizationStatus:
      return { ...state, isCampaignOptimizationOn: !state.isCampaignOptimizationOn }
    case actions.setAdCampaignOptimizationStatus:
      return { ...state, isAdCampaignOptimizationOn: !state.isAdCampaignOptimizationOn }
    case actions.resetMarketingCampaignStatus: {
      const { event } = action.payload
      return {
        ...state,
        isCampaignOptimizationOn:
          event?.campaign_optimisation_status != CampaignOptimizationStatus.paused,
        isAdCampaignOptimizationOn:
          event?.ad_campaign_optimisation_status != CampaignOptimizationStatus.paused,
        isPastEvent: moment(event?.date) < moment(),
      }
    }
    default:
      return state
  }
}

export const asyncActionHandlers = {
  [actions.fetchCampaignStatusAsync]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: actions.setLoading, payload: true })
        const { eventId } = action.payload
        await fetchCampaignStatusAsync(eventId, dispatch)
      },
  [actions.toggleCampaignStatusAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        dispatch({ type: actions.setLoading, payload: true })
        const { eventId } = action.payload
        const { campaignStatus } = getState()
        // Toggle off what's actually displayed (getClientCampaignState's
        // hasActiveCampaign), not the raw override field — otherwise a
        // null clientCampaignStatus with active platforms would show
        // "pause" in the UI but flip to ACTIVE on click.
        const newStatus = getClientCampaignState(campaignStatus).hasActiveCampaign
          ? ClientCampaignStatus.paused
          : ClientCampaignStatus.active

        const { error, status } = await campaignStatusApi.updateAsync(eventId, {
          clientCampaignStatus: newStatus,
        })
        // eslint-disable-next-line eqeqeq
        if (error || status != 204) {
          dispatch({ type: actions.setError, payload: codifyCampaignStatusError(error) })
          return
        }

        await fetchCampaignStatusAsync(eventId, dispatch)
      },
  // Pauses/resumes Google or Meta independently — the per-platform override
  // block PUT /campaign_status already supported but nothing dispatched
  // until this task.
  [actions.togglePlatformCampaignStatusAsync]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: actions.setLoading, payload: true })
        const { eventId, platform, newStatus } = action.payload

        const { error, status } = await campaignStatusApi.updateAsync(eventId, {
          [platform]: { campaign_status: newStatus },
        })
        // eslint-disable-next-line eqeqeq
        if (error || status != 204) {
          dispatch({ type: actions.setError, payload: codifyCampaignStatusError(error) })
          return
        }

        await fetchCampaignStatusAsync(eventId, dispatch)
      },
  [actions.toggleCampaignOptimizationAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { isCampaignOptimizationOn } = getState()
        dispatch({ type: actions.setCampaignOptimizationStatus })
        dispatch({ type: actions.setLoading, payload: true })
        const {
          event: { id },
        } = action.payload
        const newStatus = isCampaignOptimizationOn
          ? CampaignOptimizationStatus.paused
          : CampaignOptimizationStatus.active

        const { error, status } = await campaignStatusApi.updateOptimisationStatusAsync(
          id,
          newStatus,
        )
        // eslint-disable-next-line eqeqeq
        if (error || status != 204) {
          dispatch({ type: actions.setCampaignOptimizationStatus })
          dispatch({ type: actions.setError, payload: codifyCampaignStatusError(error) })
          return
        }

        dispatch({ type: actions.setLoading, payload: false })
      },
  [actions.toggleAdCampaignOptimizationAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { isAdCampaignOptimizationOn } = getState()
        dispatch({ type: actions.setAdCampaignOptimizationStatus })
        dispatch({ type: actions.setLoading, payload: true })
        const {
          event: { id },
        } = action.payload
        const newStatus = isAdCampaignOptimizationOn
          ? CampaignOptimizationStatus.paused
          : CampaignOptimizationStatus.active

        const { error, status } = await campaignStatusApi.updateAdOptimisationStatusAsync(
          id,
          newStatus,
        )
        // eslint-disable-next-line eqeqeq
        if (error || status != 204) {
          dispatch({ type: actions.setAdCampaignOptimizationStatus })
          dispatch({ type: actions.setError, payload: codifyCampaignStatusError(error) })
          return
        }

        dispatch({ type: actions.setLoading, payload: false })
      },
}

async function fetchCampaignStatusAsync(eventId, dispatch) {
  const { data, error, status } = await campaignStatusApi.fetchAsync(eventId)
  // eslint-disable-next-line eqeqeq
  if (error || status != 200) {
    dispatch({ type: actions.setError, payload: codifyCampaignStatusError(error) })
    return
  }

  dispatch({ type: actions.setCampaignStatus, payload: data })
}

export const isChangeCampaignStatusRunning = (state) =>
  isCampaignStatusTaskInFlight(state.campaignStatus?.task)

export const isChangePlatformCampaignStatusRunning = (state, platform) =>
  isCampaignStatusTaskInFlight(state.campaignStatus?.[platform]?.task)

// Cross-platform aggregate view for the existing combined activate/pause
// switch — mirrors v3's hasActiveMarketingPlatformCampaign/
// hasEndedMarketingPlatformCampaign (api/models/event.js), but off the v4
// mapper's top-level clientCampaignStatus/campaignStopTime fields instead
// of a flat event object.
export function getClientCampaignState(campaignStatus) {
  if (!campaignStatus) {
    return { hasMarketingCampaign: false, hasActiveCampaign: false, hasEndedCampaign: false }
  }

  const hasEndedCampaign =
    !!campaignStatus.campaignStopTime && moment(campaignStatus.campaignStopTime).isBefore(moment())
  // `clientCampaignStatus` is only ever set once an operator has explicitly
  // PUT a combined activate/pause — a campaign that's live purely from
  // platform sync (freshly seeded/published, no manual toggle yet) leaves it
  // null. Treat null as "no override" and defer to the platforms' own
  // synced state instead of reading it as paused.
  const hasActiveCampaign =
    !hasEndedCampaign &&
    (campaignStatus.clientCampaignStatus === ClientCampaignStatus.active ||
      (campaignStatus.clientCampaignStatus == null &&
        (campaignStatus.google?.hasActive || campaignStatus.meta?.hasActive)))

  return {
    hasMarketingCampaign: campaignStatus.hasAnyMarketingCampaign,
    hasActiveCampaign,
    hasEndedCampaign,
  }
}
